import { useState, useEffect, type SyntheticEvent } from 'react';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import Typography from 'carbon-react/esm/components/typography';
import Divider from 'carbon-react/lib/components/divider';
import Form from 'carbon-react/esm/components/form';
import Message from 'carbon-react/esm/components/message';
import Textbox from 'carbon-react/esm/components/textbox';
import { Checkbox } from 'carbon-react/esm/components/checkbox';
import Pill from 'carbon-react/esm/components/pill';
import useIsOctaveCodeValid from '../../../hooks/useIsOctaveCodeValid';
import { useUserInfos } from '../../../hooks/useUserInfos';
import { ValidationResult } from '../../../services/EdiOctaveTiersService';
import { useResponsive } from '../../../hooks/useResponsive';
import RegistrationSuccessDialog from './registrationSuccessDialog';
import {
  buildRegisterFormSchema,
  type RegisterFormValues,
} from '../../../types/schemas/register.schema';

const RegisterTab = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [clientCode, setClientCode] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isRegisteredUserAdmin, setIsRegisteredUserAdmin] = useState(false);
  const { validateLicense, loading } = useIsOctaveCodeValid();
  const { userInfos, refetchUserInfo, isRegistered, handleLogout } = useUserInfos();
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isRegistered()) {
      setIsValidated(true);
      setClientCode(userInfos!.OctaveCode!);
    }
  }, [userInfos, isRegistered]);

  const isFormDisabled = Boolean(userInfos?.OctaveCode) || isValidated;
  const shouldShowAdditionalFields = !userInfos?.OctaveCode && !isValidated;

  const [fieldErrors, setFieldErrors] = useState({
    clientCode: '',
    serialNumber: '',
    isChecked: '',
  });

  const validateForm = (): RegisterFormValues | null => {
    const schema = buildRegisterFormSchema(shouldShowAdditionalFields);
    const result = schema.safeParse({ clientCode, serialNumber, isChecked });

    if (result.success) {
      setFieldErrors({ clientCode: '', serialNumber: '', isChecked: '' });
      return result.data;
    }

    const newErrors = { clientCode: '', serialNumber: '', isChecked: '' };
    result.error.issues.forEach(issue => {
      const field = issue.path[0] as keyof typeof newErrors;
      if (field in newErrors) {
        newErrors[field] = issue.message;
      }
    });
    setFieldErrors(newErrors);
    return null;
  };

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError(null);

    const validatedForm = validateForm();
    if (!validatedForm) {
      return;
    }

    const validationResult: ValidationResult = await validateLicense(
      validatedForm.clientCode,
      validatedForm.serialNumber ?? '',
    );

    if (validationResult?.isValid) {
      setIsValidated(true);
      const refreshedUserInfo = await refetchUserInfo();
      setIsRegisteredUserAdmin(
        Boolean(refreshedUserInfo?.canViewAdminMenuLink && !refreshedUserInfo.IsExploit),
      );
      setIsSuccessDialogOpen(true);
    } else if (validationResult?.error) {
      setValidationError(validationResult.error);
    }
  };

  const isFormValid = () => {
    const result = buildRegisterFormSchema(shouldShowAdditionalFields).safeParse({
      clientCode,
      serialNumber,
      isChecked,
    });
    return result.success;
  };

  return (
    <div>
      <Box bg="white" p={isMobile ? '16px' : '23px'} height="calc(100% - 64px)">
        <Typography variant="h2" mb={isMobile ? 12 : 16}>
          Référencement
        </Typography>
        <Box
          marginLeft="auto"
          marginRight="auto"
          display="flex"
          maxWidth={900}
          align-items="flex-start"
          flexDirection="row"
          borderRadius="borderRadius000"
          gap="var(--spacing800)"
        >
          <Box maxWidth="640px" border-radius="var(--borderRadius000)">
            <Typography variant="h3" mb={24}>
              Informations d&apos;authentification
            </Typography>
            <Typography variant="p">
              {isFormDisabled ? (
                // Carbon 161 marks the whole prop as deprecated, although only XL is deprecated and L remains supported.
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- Keep the supported L size until Carbon narrows its type annotation.
                <Pill variant="green" size="L" fill mr={1}>
                  Votre licence a été activée avec succès.
                </Pill>
              ) : (
                'Veuillez compléter votre compte avec les informations ci-dessous, indispensables à votre authentification.'
              )}
            </Typography>
            {validationError && (
              <Message
                open
                variant="error"
                title="Erreur de référencement"
                onDismiss={() => setValidationError(null)}
                mb={3}
              >
                {validationError}
              </Message>
            )}
            <Form
              onSubmit={handleSubmit}
              leftSideButtons={
                !isFormDisabled && (
                  <Button onClick={handleLogout} variantType="secondary">
                    Annuler
                  </Button>
                )
              }
              saveButton={
                !isFormDisabled && (
                  <Button
                    variantType="primary"
                    type="submit"
                    disabled={!isFormValid() || loading || isFormDisabled}
                  >
                    {loading ? 'Validation...' : 'Valider'}
                  </Button>
                )
              }
              fieldSpacing={5}
            >
              <Textbox
                label="Code client Sage"
                name="clientCode"
                value={clientCode}
                onChange={e => {
                  setClientCode(e.target.value);
                  setValidationError(null);
                  setFieldErrors(prev => ({ ...prev, clientCode: '' }));
                }}
                disabled={isFormDisabled}
                error={fieldErrors.clientCode}
              />
              {shouldShowAdditionalFields && (
                <>
                  <Textbox
                    label="Numéro de série d'un de vos logiciels Sage ou numéro de facture Sage"
                    name="serialNumber"
                    value={serialNumber}
                    onChange={e => {
                      setSerialNumber(e.target.value);
                      setValidationError(null);
                      setFieldErrors(prev => ({ ...prev, serialNumber: '' }));
                    }}
                    disabled={isFormDisabled}
                    error={fieldErrors.serialNumber}
                  />
                  <Checkbox
                    label={
                      <>
                        J&apos;accepte les{' '}
                        <a
                          href="https://www.sage.com/fr-fr/informations-legales/conditions-generales/"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="conditions générales d'utilisation (ouvre dans un nouvel onglet)"
                          onClick={event => event.stopPropagation()}
                        >
                          conditions générales d&apos;utilisation
                        </a>
                      </>
                    }
                    name="checkbox-required"
                    checked={isChecked}
                    onChange={e => {
                      setIsChecked(e.target.checked);
                      setFieldErrors(prev => ({ ...prev, isChecked: '' }));
                    }}
                    disabled={isFormDisabled}
                    error={fieldErrors.isChecked}
                  />
                </>
              )}
            </Form>
            <Divider type="horizontal" mt={7} mb={7} />
          </Box>
        </Box>
      </Box>
      <RegistrationSuccessDialog
        isAdmin={isRegisteredUserAdmin}
        isOpen={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
      />
    </div>
  );
};

export default RegisterTab;
